import { ExternalIntegration, ExternalIntegrationEnum } from 'api/clients/ExternalIntegrationsClient'
import { ScheduledPostTarget } from 'api/clients/ScheduledPostTargetsClient'
import Styles from './ScheduledPostTargetRow.module.scss'
import ToggleButton from 'common/ToggleButton/ToggleButton'
import { PlatformIcon } from 'screens/ScheduledPosts/PlatformIcon'
import { Text } from 'blaze-ui/Text/Text'
import { ProviderToName } from 'components/ArticlePostPreview/PostPreviewBody/IntegrationsDrawer/constants'
import { getInitials } from 'utils'
import Avatar from 'common/Avatar/Avatar'
import cx from 'classnames'
import { Button } from 'blaze-ui/Button'
import UserProfileAdd2 from 'blaze-ui/icons/20/UserProfileAdd2'
import { useInitiateOauth } from 'hooks/useInitiateOauth'
import dayjs from 'dayjs'
import AlertTriangle from 'blaze-ui/icons/20/AlertTriangle'
import ArrowRefresh from 'blaze-ui/icons/20/ArrowRefresh'
import { EyeOpen, Send1, ArrowRotateLeft2, AudioSettings } from 'blaze-ui/icons'
import { Tooltip } from 'components/Tooltip/Tooltip'
import { useErrorAction } from 'components/ArticlePostPreview/PostPreviewBody/hooks'
import { ScheduledPostErrorType } from 'api/clients/ScheduledPostsClient'
import { useExternalIntegrations } from 'components/ExportModal/ExternalIntegrations/ExternalIntegrations'
import { getTargetActionConfig } from 'components/ScheduledPostTargetsPreview/utils/getTargetActionConfig'
import { Spinner } from 'common/Spinner/Spinner'
import { InitiateOauthTrigger } from 'components/ConnectIntegrationTrigger/InitiateOauthTrigger'
import { DesignChannel } from 'components/NewFileModal/Types'
import { getChannelForPlatform } from 'screens/Calendar/FullScreenPreview/utils'
import { PostSettingsModal } from 'components/PostSettingsModal/PostSettingsModal'
import { useModals } from 'almanac-ui/Modal/ModalStack'

interface ValidationResult {
  isValid: boolean
  errors: string[]
}

interface ScheduledPostTargetRowProps {
  scheduledPostTarget?: ScheduledPostTarget
  target?: ScheduledPostTarget
  platform: ExternalIntegrationEnum
  isEnabled: boolean
  onToggle: (isChecked: boolean) => void
  isAvailable: boolean
  account?: ExternalIntegration
  showPostDateTime?: boolean
  onRefreshConnection?: (target: ScheduledPostTarget) => void
  postAt?: string
  // New props for action button functionality
  validation?: ValidationResult
  isAlreadyPosted?: boolean
  onPublish?: (targetId: number) => void
  onPublishWithoutTarget?: () => void
  onViewPost?: (url: string) => void
  onRetry?: (targetId: number) => void
  useActionButtons?: boolean
  isVideoCrossPost?: boolean
}

const ERROR_CLASS_TO_ERROR_TYPE = {
  'IntegrationErrors::AuthenticationError': ScheduledPostErrorType.AuthenticationError,
  'IntegrationErrors::AuthorizationError': ScheduledPostErrorType.AuthorizationError,
  'IntegrationErrors::UnknownError': ScheduledPostErrorType.UnknownError,
  'IntegrationErrors::ValidationError': ScheduledPostErrorType.MediaFormatError,
}

export const ScheduledPostTargetRow = ({
  target,
  platform,
  isEnabled,
  onToggle,
  isAvailable,
  account,
  showPostDateTime = false,
  postAt,
  validation,
  isAlreadyPosted = false,
  onPublish,
  onPublishWithoutTarget,
  onViewPost,
  onRetry,
  useActionButtons = false,
  isVideoCrossPost = false,
}: ScheduledPostTargetRowProps) => {
  const { initiateOauth } = useInitiateOauth()
  const { data: externalIntegrations } = useExternalIntegrations()
  const { openModal } = useModals()
  const isFailed = target?.status === 'failed'
  const hasTarget = !!target

  const { errorAction } = useErrorAction({
    integration: externalIntegrations?.external_integrations.find(
      (integration) => integration.id === target?.external_integration_id,
    ),
    errorType: ERROR_CLASS_TO_ERROR_TYPE[target?.error_class as keyof typeof ERROR_CLASS_TO_ERROR_TYPE],
  })

  const getPostTypeName = (platform: ExternalIntegrationEnum): string => {
    if (isVideoCrossPost) {
      const channel = getChannelForPlatform(platform)

      if (channel === DesignChannel.InstagramReel) {
        return 'Instagram Reel'
      }

      if (channel === DesignChannel.YoutubeShort) {
        return 'YouTube Short'
      }
    }

    return `${ProviderToName[platform]} Post`
  }

  // Get action configuration when using action buttons
  const actionConfig =
    useActionButtons && validation && onPublish && onPublishWithoutTarget && onViewPost && onRetry
      ? getTargetActionConfig({
          platform,
          target,
          isAvailable,
          validation,
          isLoading: false,
          isAlreadyPosted,
          isDisabled: false,
          hasTarget,
          onConnect: () => initiateOauth(platform),
          onToggle: (_, isChecked) => onToggle(isChecked),
          onPublish,
          onPublishWithoutTarget,
          onViewPost,
          onRetry,
          onReauth: (platform: ExternalIntegrationEnum) => initiateOauth(platform),
        })
      : null

  const getActionElement = () => {
    if (!actionConfig) return null

    const wrapWithTooltip = (element: React.ReactNode) => {
      if (actionConfig.buttonTooltip) {
        return (
          <Tooltip label={actionConfig.buttonTooltip} hide={!actionConfig.buttonTooltip}>
            <div>{element}</div>
          </Tooltip>
        )
      }
      return element
    }

    switch (actionConfig.type) {
      case 'loading':
        return (
          <div>
            <Spinner />
          </div>
        )

      case 'posting':
        return <span className={Styles.label}>Posting</span>

      case 'connect':
        return wrapWithTooltip(
          <InitiateOauthTrigger popUp provider={platform} onInitiatedOauth={() => initiateOauth(platform)}>
            <Button variant="secondary" frontIcon={UserProfileAdd2}>
              Connect account
            </Button>
          </InitiateOauthTrigger>,
        )

      case 'post-now':
        return wrapWithTooltip(
          <Button
            variant="secondary"
            frontIcon={Send1}
            onPress={actionConfig.onPress}
            isDisabled={actionConfig.disabled}
          >
            Post Now
          </Button>,
        )

      case 'view-post':
        return wrapWithTooltip(
          <Button variant="secondary" size="sm" frontIcon={EyeOpen} onPress={actionConfig.onPress}>
            View Post
          </Button>,
        )

      case 'retry':
        return wrapWithTooltip(
          <Button variant="secondary" size="sm" frontIcon={ArrowRotateLeft2} onPress={actionConfig.onPress}>
            Retry
          </Button>,
        )

      case 'toggle':
      default:
        return wrapWithTooltip(
          <div className={Styles.toggleButton}>
            <ToggleButton
              name={`${platform}-toggle`}
              checked={hasTarget}
              disabled={actionConfig.disabled}
              handleChange={(e) => {
                e.stopPropagation()
                actionConfig.onPress?.()
              }}
              size="sm-md"
            />
          </div>,
        )
    }
  }

  const handlePostSettingsClick = () => {
    if (!target?.scheduled_post_id || !target?.id) return

    const channel = getChannelForPlatform(platform)

    if (!channel) return
    openModal(PostSettingsModal, {
      channel,
      scheduledPostId: target.scheduled_post_id,
      scheduledPostTargetId: target.id,
    })
  }

  const content = (
    <div className={cx(Styles.root, isFailed && Styles.failed)}>
      <div className={Styles.column}>
        <div className={Styles.post}>
          {/* Show toggle button only when not using action buttons */}
          {!useActionButtons && (
            <ToggleButton
              disabled={!isAvailable}
              name={`${platform}-toggle`}
              checked={isEnabled}
              size="sm-md"
              handleChange={(e) => {
                e.stopPropagation()
                onToggle(e.target.checked)
              }}
            />
          )}
          <div className={cx(Styles.postDetails, !isEnabled && Styles.disabled)}>
            <PlatformIcon platform={platform} size={28} />
            <Text variant="smallList" color="var(--dark-80)">
              {getPostTypeName(platform)}
            </Text>
          </div>
        </div>
      </div>
      <div className={Styles.column}>
        {account ? (
          <div className={cx(Styles.account, !isEnabled && Styles.disabled)}>
            <Avatar size={28} label={getInitials(account?.name)} url={account?.picture} />
            <Text variant="smallList" color="var(--dark-80)">
              {account?.name}
            </Text>
          </div>
        ) : (
          <div className={Styles.account}>
            <Avatar size={28} />
            <Text variant="smallList" color="var(--dark-40)">
              No account connected
            </Text>
          </div>
        )}
      </div>

      {/* Date/time column for scheduled posts */}
      {showPostDateTime && (
        <div className={cx(Styles.column, Styles.postDateTimeColumn)}>
          {postAt && (
            <div className={Styles.postDateTime}>
              <Text variant="placeholder" color="var(--dark-80)">
                {dayjs(postAt).format('ddd MMM D')}
              </Text>
              <Text variant="placeholder" color="var(--dark-40)">
                {dayjs(postAt).format('h:mma')}
              </Text>
            </div>
          )}
        </div>
      )}

      {/* Action column for posted targets */}
      {useActionButtons && (
        <div className={Styles.actionColumn}>
          {actionConfig?.showPostSettings && (
            <Button
              onPress={handlePostSettingsClick}
              variant="ghost"
              color="var(--dark-80)"
              frontIcon={AudioSettings}
              isDisabled={actionConfig?.disabled || !hasTarget}
              data-testid="post-settings-button"
            >
              Post Settings
            </Button>
          )}
          {getActionElement()}
        </div>
      )}

      {/* Error message for failed targets (only when not using action buttons) */}
      {!useActionButtons && isFailed && target?.error_message && (
        <div className={Styles.errorSection}>
          <div className={Styles.errorMessage}>
            <AlertTriangle color="var(--negative-60)" size={25} />
            <Text variant="placeholder" color="var(--negative-60)">
              {target.error_message}
            </Text>
          </div>
          {errorAction && errorAction()?.onPress && (
            <Button size="sm" variant="secondary" frontIcon={ArrowRefresh} onPress={errorAction()?.onPress}>
              {errorAction()?.label}
            </Button>
          )}
        </div>
      )}
    </div>
  )

  if (actionConfig?.componentTooltip) {
    return <Tooltip label={actionConfig.componentTooltip}>{content}</Tooltip>
  }

  return content
}
